import { Injectable, Inject, ElementRef } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { DOCUMENT } from '@angular/common';
import { DomSanitizer } from '@angular/platform-browser';
import { MatIconRegistry } from '@angular/material/icon';

import { environment } from 'environments/environment';
import { Credentials, FILE_TYPE, MenuItem, MenuItemType, OrderBy } from '@shared/types';
import { Router } from '@angular/router';
import { SharedHttpService } from '../api/shared-http/shared-http.service';
import { HttpHeaders } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { PublicPortalService } from '../api/public-portal/public-portal.service';

@Injectable({
  providedIn: 'root'
})
export class FeaturesService {
  authenticated: BehaviorSubject<boolean> = new BehaviorSubject(null);
  forbidden: BehaviorSubject<boolean> = new BehaviorSubject(null);
  page: BehaviorSubject<string> = new BehaviorSubject(null);
  templateHidden: BehaviorSubject<boolean> = new BehaviorSubject(false);
  fixedMenu: BehaviorSubject<Array<MenuItem>> = new BehaviorSubject(null);
  navigatedToTop: BehaviorSubject<boolean> = new BehaviorSubject(null);
  footerTextColor: BehaviorSubject<string> = new BehaviorSubject(null);
  adminAuthenticated:BehaviorSubject<boolean>=new BehaviorSubject(null);
  isAdmin:boolean=JSON.parse(this.getCredential('isAdmin'));
  private API: string;
  private apiUrl = environment.apiUrl;
  private apiService = 'file';
  private port = '8004';
  
  constructor(
    @Inject(DOCUMENT) private document,
    private matIconRegistry: MatIconRegistry,
    private sanitizer: DomSanitizer,
    private router: Router,
    private httpService: SharedHttpService,
    private publicPortalService: PublicPortalService
  ) { 
    this.API = environment.production
      ? this.apiUrl.replace('{service}', this.apiService)
      : this.apiUrl.replace('{port}', this.port);
  }

  getCompany(): string {
    const year = new Date().getFullYear();
    return `${year}, Apogee Telecom LLC`;
    //return '';
  }

  getCompanyLogo(color: string): string {
    return /ffffff/i.test(color)
      ? 'assets/icons/logo.png'
      : 'assets/icons/logo-black.png';
  }

  setCurrentPage(page: string): void {
    this.page.next(page);
  }

  navigateToTop(): void {
    window.scrollTo(0, 0);
    this.document.body.scrollTop = 0; // For Safari
    this.document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
  }

  resetTabNavigation(firstEl: ElementRef<any>): void {
    firstEl.nativeElement.focus();
  }

  logIn(): void {
    const { location } = this.document;
    const origin = `${this.document.domain}${location.port ? `:${location.port}` : ''
      }`;

    this.document.location.href = environment.login(origin);
  }

  getDomainPlaylist():string{
    console.warn(location, this.document.domain);
    const domain=(this.document.domain.includes('localhost') || this.document.domain.includes('newuatdomain'))
     ? 'http://chia.newuatdomain.com' : 'https://chia.apogee-ces.com'
    return domain;
  }

  loggedIn(): void {
    this.authenticated.next(true);
  }

  logOut(): void {
    localStorage.clear();
    this.router.navigate(['/home']).finally(() => {
      this.authenticated.next(false);
      this.fixedMenu.next(null);
    });
  }

  logOutAdmin(){
    localStorage.clear();
    this.router.navigate(['/admin/home']).finally(()=>{
      this.authenticated.next(false);
      this.fixedMenu.next(null);
    })
  }

  setCredentials(credentials: Credentials) {
    for (const key in credentials) {
      if (credentials.hasOwnProperty(key)) {
        localStorage.setItem(key, credentials[key]);
      }
    }
    this.loggedIn();
  }

  setAdmin(isAdmin:boolean){
    localStorage.setItem('isAdmin',JSON.stringify(isAdmin));
  }

  getCredential(key: string): string {
    return localStorage.getItem(key);
  }

  hideTemplate(): void {
    this.templateHidden.next(true);
  }

  showTemplate(): void {
    this.templateHidden.next(false);
  }

  formatDate(date: Date): string {
    const yyyy = date.getFullYear();
    const mm =
      date.getMonth() + 1 < 10
        ? `0${date.getMonth() + 1}`
        : date.getMonth() + 1;
    const dd = date.getDate() < 10 ? `0${date.getDate()}` : date.getDate();

    return `${mm}/${dd}/${yyyy}`;
  }

  setFixedToolbarMenu(portalName: string): void {
    const menus: Array<MenuItem> = [
      {
        id: null,
        name: 'HOME',
        value: `/local-portal/${portalName}/home`,
        menus: [],
        type: MenuItemType.LINK,
        parentId: null,
        order: 1
      },
      {
        id: null,
        name: 'MANAGE',
        value: `/local-portal/${portalName}/manage`,
        menus: [],
        type: MenuItemType.LINK,
        parentId: null,
        order: 1
      }
    ];
    this.fixedMenu.next(menus);
  }

  setFixedToolbarMenuAdmin(): void {
    const menus: Array<MenuItem> = [
      {
        id: 1,
        name: 'HOME',
        value: `/admin/home`,
        menus: [],
        type: MenuItemType.LINK,
        parentId: null,
        order: 1
      },
      // {
      //   id: 2,
      //   name: 'CREATE-AN-AD',
      //   value: `/admin/create-ad`,
      //   menus: [],
      //   type: MenuItemType.LINK,
      //   parentId: null,
      //   order: 2
      // },
      {
        id: 2,
        name: 'GUIDELINES',
        value: `/admin/guidelines`,
        menus: [],
        type: MenuItemType.LINK,
        parentId: null,
        order: 2
      },
      {
        id: 3,
        name: 'SUBMIT',
        value: `/admin/submit`,
        menus: [],
        type: MenuItemType.LINK,
        parentId: null,
        order: 3
      },
      {
        id: 4,
        name: 'MANAGE',
        value: `/admin/manage`,
        menus: [],
        type: MenuItemType.LINK,
        parentId: null,
        order: 4
      },
    ];
    this.fixedMenu.next(menus);
  }

  addSvgIcon(icon: string, iconPath: string) {
    const safePath = this.sanitizer.bypassSecurityTrustResourceUrl(iconPath);
    this.matIconRegistry.addSvgIcon(icon, safePath);
  }

  getPageMetadata(url: string): Observable<any> {
    const parser = new DOMParser();
    return this.publicPortalService.getHTMLPage(url).pipe(
      map(html => {
        let title = null;
        let description = null;
        let image: string = null;
        if (html) {
          const doc = parser.parseFromString(html, 'text/html');
          const meta = Array.from(doc.getElementsByTagName('meta'));

          if (meta && meta.length > 0) {
            title = this._getMetaData(meta, 'title');
            description = this._getMetaData(meta, 'description');
            image = this._getMetaData(meta, 'image') || this._getImageData(doc);
            if (/^\//.test(image)) {
              image = `${url}${image}`;
            }
          }
        }

        return {
          title,
          description,
          image:
            image && image.includes(location.origin)
              ? image.replace(location.origin, url)
              : image
        };
      })
    );
  }

  getFileType(link: string): FILE_TYPE {
    const imageRegExp = new RegExp(/^((https|http):\/\/)?.*(\.jpg|\.jpeg)$/, 'i');
    const videoRegExp = new RegExp(/^((https|http):\/\/)?.*(\.mp4)$/, 'i');
    const youtubeRegExp = new RegExp(/youtube|youtu.be/, 'i');
    const pageRegExp = new RegExp(/^((https|http):\/\/)?.*\.([a-z]{2,3})(\/.*)*/, 'i');

    if (imageRegExp.test(link)) {
      return FILE_TYPE.Image;
    }
    if (videoRegExp.test(link)) {
      return FILE_TYPE.Video;
    }

    if (youtubeRegExp.test(link)) {
      return FILE_TYPE.YouTube;
    }

    if (pageRegExp.test(link)) {
      return FILE_TYPE.Page;
    }

    return FILE_TYPE.Unknown;
  }

  isMobile(): boolean {
    let check = false;
    ((a) => {
      if (/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i.test(a) || /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(a.substr(0, 4))) check = true;
    })(navigator.userAgent || navigator.vendor || (window as any).opera);
    return check;
  }

  sortByKey(key: string, values = [], orderBy: OrderBy): any[] {
    // Note(Danny): Slice need it to create a new copy of values, due to frozen array...
    return values.slice().sort((valA, valB) => {
      const a = /\d{2}\/\d{2}\/\d{4}/.test(valA[key]) ? new Date(valA[key]).getTime() : valA[key].toUpperCase();
      const b = /\d{2}\/\d{2}\/\d{4}/.test(valB[key]) ? new Date(valB[key]).getTime() : valB[key].toUpperCase();
      return this._orderBy(a, b, orderBy);
    });
  }

  adBlockerPresent(): Promise<any> {
    const adSenseUrl = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js';
    return fetch(new Request(adSenseUrl, {
      method: 'HEAD',
      mode: 'no-cors'
    }));
  }

  _orderBy(a: any, b: any, order: OrderBy): any {
    const isNumber = Number.isInteger(a);
    switch (order) {
      case OrderBy.ASC:
        return isNumber ? a - b : a > b ? 1 : a < b ? -1 : 0;
      case OrderBy.DESC:
        return isNumber ? b - a : b > a ? 1 : b < a ? -1 : 0;
    }
  }

  _getMetaData(meta: Array<any>, tag: string): string {
    const data = meta.find(m => {
      if (
        m.name === tag || // General
        m.getAttribute('property') === `og:${tag}` || // OpenGraph
        m.getAttribute('property') === `twitter:${tag}` // Twitter
      ) {
        return m;
      }
    });
    return data ? data.content : null;
  }

  _getImageData(doc: Document): string {
    let img;
    if (doc.getElementsByTagName('img').length > 0) {
      // JPG - has priority
      img = Array.from(doc.getElementsByTagName('img')).find(i =>
        /(\.jpg|\.jpeg)$/.test(i.src)
      );
      // PNG - Last chance to get an image
      img = img
        ? img
        : Array.from(doc.getElementsByTagName('img')).find(i =>
          /(\.png)$/.test(i.src)
        );
    }
    return img ? img.src : null;
  }


}
